import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';

const milestones = [
  { year: '2019', title: '品牌创立', desc: '91美正式成立，致力于打造值得信赖的美业服务平台' },
  { year: '2020', title: '快速成长', desc: '入驻门店突破500家，覆盖北上广深等一线城市' },
  { year: '2021', title: '品类拓展', desc: '从美容美发拓展至美甲美体，实现美业全品类覆盖' },
  { year: '2022', title: '服务升级', desc: '上线在线预约系统，累计服务用户突破100万' },
  { year: '2023', title: '品质认证', desc: '建立平台品质认证体系，严选优质门店' },
  { year: '2024', title: '全国布局', desc: '业务覆盖全国30+城市，合作门店超3000家' },
  { year: '2025', title: '智能推荐', desc: 'AI智能匹配系统上线，精准推荐最适合的服务' },
  { year: '2026', title: '持续创新', desc: '不断完善服务体系，让每一位用户都能轻松变美' },
];

const valuesList = [
  {
    icon: 'ri-heart-line',
    title: '用户至上',
    desc: '始终以用户需求为核心，用心做好每一次服务',
  },
  {
    icon: 'ri-shield-star-line',
    title: '品质第一',
    desc: '严格的商家准入标准和品质审核，确保服务品质',
  },
  {
    icon: 'ri-lightbulb-line',
    title: '创新驱动',
    desc: '运用技术力量持续优化平台体验，让美丽触手可及',
  },
  {
    icon: 'ri-hand-heart-line',
    title: '诚信为本',
    desc: '真实评价、透明价格，建立用户信任的美业生态',
  },
];

const stats = [
  { value: '3000+', label: '合作门店' },
  { value: '100万+', label: '服务用户' },
  { value: '30+', label: '覆盖城市' },
  { value: '98%', label: '好评率' },
];

export default function About() {
  const { t } = useTranslation();
  const [formState, setFormState] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = form.querySelector<HTMLInputElement>('input[name="website_alt"]');
    if (honeypot && honeypot.value.trim() !== '') {
      setStatus('success');
      return;
    }

    if (formState.message.length > 500) {
      setErrorMsg('留言内容不能超过500字');
      setStatus('error');
      return;
    }

    setStatus('submitting');
    setErrorMsg('');

    try {
      const formData = new FormData(form);
      if (honeypot) {
        formData.delete('website_alt');
      }
      const urlEncoded = new URLSearchParams(formData as unknown as Record<string, string>).toString();
      const res = await fetch('https://readdy.ai/api/form/d9pkf91n2trhb35gvh20', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: urlEncoded,
      });
      const responseText = await res.text();
      let parsed: Record<string, unknown> | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        // ignore parse errors
      }

      if (res.ok && parsed && (parsed as { code?: string }).code === 'OK') {
        setStatus('success');
        setFormState({ name: '', email: '', phone: '', message: '' });
      } else {
        const serverMsg =
          (parsed as { meta?: { message?: string; detail?: string }; message?: string })?.meta?.message ||
          (parsed as { meta?: { message?: string; detail?: string }; message?: string })?.meta?.detail ||
          (parsed as { message?: string })?.message ||
          responseText;
        setErrorMsg(typeof serverMsg === 'string' ? serverMsg : '提交失败，请稍后重试');
        setStatus('error');
      }
    } catch {
      setErrorMsg('网络错误，请稍后重试');
      setStatus('error');
    }
  };

  return (
    <>
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'AboutPage',
            name: '关于91美',
            description:
              '91美是您值得信赖的美业服务平台，汇聚海量优质美容、美发、美甲、美体项目。',
            url: 'https://91mei.com/about',
            isPartOf: {
              '@type': 'WebSite',
              name: '91美',
              url: 'https://91mei.com',
            },
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              {
                '@type': 'ListItem',
                position: 1,
                name: '首页',
                item: 'https://91mei.com',
              },
              {
                '@type': 'ListItem',
                position: 2,
                name: '关于我们',
                item: 'https://91mei.com/about',
              },
            ],
          }),
        }}
      />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-16">
          <Breadcrumb
            items={[{ label: t('breadcrumb.about') }]}
          />

          {/* Hero */}
          <section className="relative h-[350px] md:h-[420px] overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=Elegant%20beauty%20and%20wellness%20brand%20office%20interior%20with%20warm%20ambient%20lighting%2C%20modern%20minimalist%20design%2C%20fresh%20flowers%2C%20cream%20and%20soft%20beige%20tones%2C%20professional%20atmosphere%2C%20natural%20elements&width=1600&height=700&seq=91mei-about-hero&orientation=landscape"
              alt="关于91美"
              className="absolute inset-0 w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-black/25"></div>
            <div className="relative z-10 h-full flex flex-col items-center justify-center text-center px-4">
              <h1 className="font-heading text-3xl md:text-4xl text-white mb-3">
                {t('about.title')}
              </h1>
              <p className="text-sm md:text-base text-white/85 max-w-[550px]">
                {t('site.description')}
              </p>
            </div>
          </section>

          {/* Brand Story */}
          <section className="py-14 md:py-20 px-4 md:px-6 bg-background-50">
            <div className="max-w-[1280px] mx-auto">
              <div className="text-center mb-10">
                <h2 className="font-heading text-2xl md:text-3xl text-foreground-900 mb-3">
                  {t('about.brandStory')}
                </h2>
              </div>
              <div className="max-w-[800px] mx-auto text-center">
                <p className="text-foreground-600 leading-relaxed text-sm mb-4">
                  91美成立于2019年，由一群热爱美业、追求品质的行业专家共同创立。我们深知每一位爱美的你，都希望在繁忙的生活中轻松找到值得信赖的美业服务。91美应运而生，致力于打造一个连接优质美业商家与消费者的服务平台。
                </p>
                <p className="text-foreground-600 leading-relaxed text-sm mb-4">
                  从最初的美容美发起步，到如今覆盖美容、美发、美甲、美体等全品类美业服务，91美始终坚持"品质第一，用户至上"的理念。平台上所有合作门店均经过严格的资质审核和实地考察，确保每一位用户都能享受到专业、安全、舒心的服务体验。
                </p>
                <p className="text-foreground-600 leading-relaxed text-sm">
                  未来，91美将继续深耕美业服务领域，用科技的力量让美丽变得更简单、更触手可及。我们相信，每个人都值得拥有最好的美丽体验。
                </p>
              </div>
            </div>
          </section>

          {/* Stats */}
          <section className="py-14 md:py-20 px-4 md:px-6 bg-primary-500">
            <div className="max-w-[1280px] mx-auto">
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 text-center">
                {stats.map((stat) => (
                  <div key={stat.label}>
                    <div className="text-3xl md:text-4xl font-bold text-white mb-2">{stat.value}</div>
                    <div className="text-sm text-white/80">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Timeline */}
          <section className="py-14 md:py-20 px-4 md:px-6 bg-background-50">
            <div className="max-w-[1280px] mx-auto">
              <div className="text-center mb-12">
                <h2 className="font-heading text-2xl md:text-3xl text-foreground-900 mb-3">
                  发展历程
                </h2>
              </div>
              <div className="relative max-w-[900px] mx-auto">
                <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-background-300 md:-translate-x-px"></div>
                <div className="space-y-8">
                  {milestones.map((m, i) => (
                    <div
                      key={m.year}
                      className={`relative flex items-start gap-6 md:gap-10 ${
                        i % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                      }`}
                    >
                      <div className={`flex-1 ${i % 2 === 0 ? 'md:text-right' : 'md:text-left'} pl-10 md:pl-0`}>
                        <div className={`hidden md:block p-4 rounded-lg bg-background-100 border border-background-200/70`}>
                          <span className="text-xs font-bold text-accent-500 whitespace-nowrap">{m.year}</span>
                          <h4 className="text-sm font-semibold text-foreground-900 mt-1">{m.title}</h4>
                          <p className="text-xs text-foreground-500 mt-1">{m.desc}</p>
                        </div>
                        <div className="md:hidden p-4 rounded-lg bg-background-100 border border-background-200/70">
                          <span className="text-xs font-bold text-accent-500 whitespace-nowrap">{m.year}</span>
                          <h4 className="text-sm font-semibold text-foreground-900 mt-1">{m.title}</h4>
                          <p className="text-xs text-foreground-500 mt-1">{m.desc}</p>
                        </div>
                      </div>
                      <div className="absolute left-4 md:left-1/2 w-3 h-3 rounded-full bg-accent-500 border-2 border-background-50 -translate-x-1/2 mt-1.5 z-10"></div>
                      <div className="flex-1 hidden md:block"></div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* Values */}
          <section className="py-14 md:py-20 px-4 md:px-6 bg-background-100">
            <div className="max-w-[1280px] mx-auto">
              <div className="text-center mb-10">
                <h2 className="font-heading text-2xl md:text-3xl text-foreground-900 mb-3">
                  {t('about.values')}
                </h2>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {valuesList.map((v) => (
                  <div key={v.title} className="bg-background-50 rounded-lg p-6 border border-background-200/70 text-center">
                    <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center rounded-full bg-accent-100">
                      <i className={`${v.icon} text-xl text-accent-500`}></i>
                    </div>
                    <h3 className="font-semibold text-foreground-900 text-sm mb-2">{v.title}</h3>
                    <p className="text-xs text-foreground-500 leading-relaxed">{v.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* FAQ */}
          <section className="py-14 md:py-20 px-4 md:px-6 bg-background-50">
            <div className="max-w-[800px] mx-auto">
              <div className="text-center mb-10">
                <h2 className="font-heading text-2xl md:text-3xl text-foreground-900 mb-3">
                  常见问题
                </h2>
              </div>

              <script
                type="application/ld+json"
                dangerouslySetInnerHTML={{
                  __html: JSON.stringify({
                    '@context': 'https://schema.org',
                    '@type': 'FAQPage',
                    mainEntity: [
                      {
                        '@type': 'Question',
                        name: '91美平台上的门店是如何筛选的？',
                        acceptedAnswer: {
                          '@type': 'Answer',
                          text: '91美对所有入驻门店实行严格的资质审核制度，包括营业执照核验、实地考察、服务品质评估等多重筛选流程，确保每一家门店都符合平台品质标准。',
                        },
                      },
                      {
                        '@type': 'Question',
                        name: '如何通过91美预约服务？',
                        acceptedAnswer: {
                          '@type': 'Answer',
                          text: '您可以在91美平台上浏览心仪的服务项目，选择合适的时间和门店，填写基本信息后即可完成在线预约，操作简单快捷。',
                        },
                      },
                      {
                        '@type': 'Question',
                        name: '91美平台上的价格透明吗？',
                        acceptedAnswer: {
                          '@type': 'Answer',
                          text: '是的，91美坚持透明定价原则，所有服务项目均明码标价，拒绝隐形消费和强制推销，让您消费更放心。',
                        },
                      },
                    ],
                  }),
                }}
              />

              <div className="space-y-3">
                {[
                  {
                    q: '91美平台上的门店是如何筛选的？',
                    a: '91美对所有入驻门店实行严格的资质审核制度，包括营业执照核验、实地考察、服务品质评估等多重筛选流程，确保每一家门店都符合平台品质标准。',
                  },
                  {
                    q: '如何通过91美预约服务？',
                    a: '您可以在91美平台上浏览心仪的服务项目，选择合适的时间和门店，填写基本信息后即可完成在线预约，操作简单快捷。',
                  },
                  {
                    q: '91美平台上的价格透明吗？',
                    a: '是的，91美坚持透明定价原则，所有服务项目均明码标价，拒绝隐形消费和强制推销，让您消费更放心。',
                  },
                ].map((faq) => (
                  <details key={faq.q} className="bg-background-100 rounded-lg border border-background-200/70 group cursor-pointer">
                    <summary className="p-4 text-sm font-medium text-foreground-900 flex items-center justify-between list-none">
                      {faq.q}
                      <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform"></i>
                    </summary>
                    <p className="px-4 pb-4 text-sm text-foreground-600 leading-relaxed">
                      {faq.a}
                    </p>
                  </details>
                ))}
              </div>
            </div>
          </section>

          {/* Contact Form */}
          <section className="py-14 md:py-20 px-4 md:px-6 bg-background-100">
            <div className="max-w-[700px] mx-auto">
              <div className="text-center mb-10">
                <h2 className="font-heading text-2xl md:text-3xl text-foreground-900 mb-3">
                  {t('about.contact')}
                </h2>
                <p className="text-sm text-foreground-500">
                  {t('about.contactDesc')}
                </p>
              </div>

              <form
                data-readdy-form
                id="contact-form"
                onSubmit={handleSubmit}
                className="bg-background-50 rounded-lg border border-background-200/70 p-6 md:p-8"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      {t('contact.name')} <span className="text-primary-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      value={formState.name}
                      onChange={(e) => setFormState({ ...formState, name: e.target.value })}
                      placeholder={t('contact.placeholder.name')}
                      className="w-full px-4 py-2.5 text-sm rounded-md border border-background-300 bg-background-50 text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      {t('contact.email')} <span className="text-primary-500">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      value={formState.email}
                      onChange={(e) => setFormState({ ...formState, email: e.target.value })}
                      placeholder={t('contact.placeholder.email')}
                      className="w-full px-4 py-2.5 text-sm rounded-md border border-background-300 bg-background-50 text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                    />
                  </div>
                </div>

                <div className="mb-4">
                  <label htmlFor="phone" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    {t('contact.phone')}
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formState.phone}
                    onChange={(e) => setFormState({ ...formState, phone: e.target.value })}
                    placeholder={t('contact.placeholder.phone')}
                    className="w-full px-4 py-2.5 text-sm rounded-md border border-background-300 bg-background-50 text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                  />
                </div>

                <div className="mb-4">
                  <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    {t('contact.message')} <span className="text-primary-500">*</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    rows={5}
                    maxLength={500}
                    value={formState.message}
                    onChange={(e) => setFormState({ ...formState, message: e.target.value })}
                    placeholder={t('contact.placeholder.message')}
                    className="w-full px-4 py-2.5 text-sm rounded-md border border-background-300 bg-background-50 text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors resize-none"
                  ></textarea>
                  <p className="text-xs text-foreground-400 mt-1">{formState.message.length}/500</p>
                </div>

                <input
                  type="text"
                  name="website_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="absolute left-[-9999px] opacity-0 h-0 w-0 pointer-events-none"
                  style={{ position: 'absolute', left: '-9999px', opacity: 0, height: 0, width: 0, pointerEvents: 'none', tabIndex: -1 }}
                />

                <button
                  type="submit"
                  disabled={status === 'submitting'}
                  className="w-full bg-primary-500 hover:bg-primary-600 disabled:bg-primary-300 text-white py-3 rounded-md font-medium text-sm transition-all duration-200 cursor-pointer whitespace-nowrap"
                >
                  {status === 'submitting' ? t('contact.submitting') : t('contact.submit')}
                </button>

                {status === 'success' && (
                  <p className="mt-3 text-sm text-secondary-600 text-center">{t('contact.success')}</p>
                )}
                {status === 'error' && (
                  <p className="mt-3 text-sm text-primary-500 text-center">
                    {errorMsg || t('contact.error')}
                  </p>
                )}
              </form>
            </div>
          </section>
        </div>
        <Footer />
      </div>
    </>
  );
}